/**
 * Tiny shared feedback primitives for the standard loading / error / empty
 * states screen agents render around `useApi(...)` results. Styled with the
 * clean (.cl) design-system classes so they slot into any screen content area.
 */
import { useCommonT } from '../i18n';

export interface LoadingStateProps {
  label?: string;
}

/** Inline loading placeholder. */
export function LoadingState({ label }: LoadingStateProps) {
  const t = useCommonT();
  return (
    <div className="col center" style={{ padding: '40px 0', gap: 8, alignItems: 'center' }}>
      <div className="meta">{label ?? `${t('loading')}…`}</div>
    </div>
  );
}

export interface ErrorStateProps {
  /** Human-readable message (e.g. `error.message`). */
  message: string;
  /** Optional retry handler — renders a "Try again" button when provided. */
  onRetry?: () => void;
}

/** Inline error panel with optional retry. */
export function ErrorState({ message, onRetry }: ErrorStateProps) {
  const t = useCommonT();
  return (
    <div
      className="panel"
      style={{ padding: '14px 16px', borderColor: 'var(--red)', background: 'var(--red-soft)' }}
    >
      <div className="row between center gap-12">
        <div style={{ color: 'var(--red)', fontSize: 14 }}>{message}</div>
        {onRetry && (
          <button type="button" className="btn red sm" onClick={onRetry}>
            {t('try_again')}
          </button>
        )}
      </div>
    </div>
  );
}

export interface EmptyStateProps {
  title: string;
  hint?: string;
}

/** Inline empty-state placeholder. */
export function EmptyState({ title, hint }: EmptyStateProps) {
  return (
    <div className="col center" style={{ padding: '40px 0', gap: 6, alignItems: 'center' }}>
      <div className="h2">{title}</div>
      {hint && <div className="meta">{hint}</div>}
    </div>
  );
}
